"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { PageTradingCompetitionSchema } from "@/sanity/pages/PageTradingCompetitionTemplate";
import SelectionHighlight, { type SelectionGeometry } from "./SelectionHighlight";
import PrizeCard from "./prizes/PrizeCard";
import PrizeRing from "./prizes/PrizeRing";
import RollingNumber from "./prizes/RollingNumber";
import { at, useReveal } from "./motion";

/** "$200,000" selection: Figma 153×37 at 34px (mobile), 236×55 at 48px (desktop), measured against the glyphs. */
const TITLE_SELECTION = {
  mobile: {
    font: 34,
    box: [-3.31, -6.07, 153, 37],
    word: 141.34,
    handles: [[-0.75, 0, 1.5, 33], [-2, 33, 4, 4], [151, 0, 4, 4], [152.25, 4, 1.5, 33]],
  },
  desktop: {
    font: 48,
    box: [-16.44, -6, 236, 55],
    word: 205.36,
    handles: [[0, 1, 2, 48], [-2, 49, 6, 6], [232, 0, 6, 6], [234, 0, 2, 55]],
  },
} satisfies Record<string, SelectionGeometry>;

/** Desktop card spots on the 1200×766 stage (Figma "Prêmios · anel"), in rem; left column odd, right column even. */
const DESKTOP_SPOTS = [
  [0, 1.625],
  [114.375, 0],
  [0, 23.625],
  [114.375, 22],
  [0, 45.625],
  [114.375, 47.25],
  [0, 70.875],
  [114.375, 72.5],
];

/** Desktop: scroll distance per band while the stage is pinned (vh). */
const STEP_VH = 24;
/** Mobile: nav (61px) + 12px air + ring (340px) + 12px, where every card pins. */
const STACK_TOP_REM = 53.125;
/** Mobile: the gap between cards (gap-[1.5rem]). */
const STACK_GAP_REM = 1.5;
/**
 * Mobile stack, as on worldclassedge.com/leaderboard: a card fades in and rises as it enters the
 * screen (its top from 98% to 73% of the viewport height), and the pinned card shrinks to 0.9 and
 * fades out as the next one covers it (the next card from 50% to 5% of a card step away).
 */
const FADE_IN = [0.98, 0.73];
const SHRINK = [0.5, 0.05];
const RISE_REM = 1.75;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const progress = (distance: number, step: number, [from, to]: number[]) => clamp01((from * step - distance) / ((from - to) * step));

/** "**bold**" → bold spans. */
function Bold({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith("**") ? (
          <strong key={i} className="font-geist font-semibold text-[#001b14]">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

function RingCenter({ total, label, mobile }: { total: number; label?: string; mobile?: boolean }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <RollingNumber
        value={total}
        className={cn(
          "font-geist font-medium whitespace-nowrap text-[#001b14]",
          mobile ? "text-[6rem] tracking-[-0.18rem]" : "text-[9rem]",
        )}
      />
      <p
        className={cn(
          "mb-0 font-geist font-medium whitespace-nowrap text-[rgba(0,27,20,0.6)]",
          mobile ? "mt-[0.45rem] text-[1.5rem] leading-[normal] tracking-[0.09rem]" : "mt-[0.75rem] text-[1.75rem] leading-[normal] tracking-[0.14rem]",
        )}>
        {label}
      </p>
    </div>
  );
}

export default function TradingCompetitionPrizes({ data }: { data: PageTradingCompetitionSchema }) {
  const bands = data.prizesBands ?? [];
  const n = bands.length;
  const values = bands.map((b) => (b.amount ?? 0) * (b.winners ?? 1));
  const pool = values.reduce((a, b) => a + b, 0) || 1;
  const fractions = values.map((v) => v / pool);
  const reduced = useReducedMotion();
  const [headerRef, headerReveal] = useReveal();

  // ── Desktop: pinned stage, one band per scroll step ──────────────────
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(n); // as drawn until we know where the reader is

  // ── Mobile: sticky ring, sticky stack ────────────────────────────────
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardInnerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [entered, setEntered] = useState(n);
  const [stuck, setStuck] = useState(-1);

  useEffect(() => {
    if (reduced || !n) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const desktop = window.matchMedia("(min-width: 993px)").matches;
      if (desktop) {
        const track = trackRef.current;
        const stage = stageRef.current;
        if (!track || !stage) return;
        const distance = track.offsetHeight - stage.offsetHeight;
        const moved = stage.getBoundingClientRect().top - track.getBoundingClientRect().top;
        const p = distance > 0 ? Math.min(1, Math.max(0, moved / distance)) : 1;
        setRevealed(Math.min(n, Math.floor(p * (n + 1))));
      } else {
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const pin = STACK_TOP_REM * rem;
        const gap = STACK_GAP_REM * rem;
        const cards = cardRefs.current.map((el) => (el ? { top: el.getBoundingClientRect().top, height: el.offsetHeight } : null));
        let enteredCount = 0;
        let lastStuck = -1;
        cards.forEach((card, i) => {
          if (!card) return;
          const { top, height } = card;
          // a band fills its arc once its card is halfway into the stack
          if (top <= pin + height / 2) enteredCount = i + 1;
          if (top <= pin + 1) lastStuck = i;

          const shown = progress(top, window.innerHeight, FADE_IN);
          const next = cards[i + 1];
          const covered = next ? progress(next.top - pin, height + gap, SHRINK) : 0;
          const inner = cardInnerRefs.current[i];
          if (inner) {
            inner.style.opacity = String(shown * (1 - covered));
            inner.style.transform = `translateY(${(1 - shown) * RISE_REM}rem) scale(${1 - 0.1 * covered})`;
          }
        });
        setEntered(enteredCount);
        setStuck(lastStuck);
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [reduced, n]);

  if (!n) return null;

  const desktopFilled = bands.map((_, i) => i < revealed);
  const desktopTotal = values.slice(0, revealed).reduce((a, b) => a + b, 0);
  // the newest band is lit while the reveal runs; at the end the ring rests as drawn
  const desktopActive = revealed > 0 && revealed < n ? revealed - 1 : -1;

  const mobileFilled = bands.map((_, i) => i < entered);
  const mobileTotal = values.slice(0, entered).reduce((a, b) => a + b, 0);
  const mobileActive = stuck >= 0 && stuck < n - 1 ? stuck : -1;

  const cardProps = {
    cashLabel: data.prizesCashLabel,
    voucherLabel: data.prizesVoucherLabel,
    winnersText: data.prizesWinnersText,
  };

  return (
    <section
      id="prizes"
      className="relative flex scroll-mt-[7.5rem] flex-col items-center gap-[4rem] bg-white px-[2rem] pt-[7rem] pb-[8rem] lg:px-0 lg:py-[12.5rem]">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div ref={headerRef} data-reveal={headerReveal} className="flex w-full flex-col items-center gap-[1.75rem] lg:w-auto lg:gap-[2rem]">
        {data.prizesEyebrow && (
          <p style={at(0)} className="tc-rv-rise mb-0 font-blacker text-[2.125rem] leading-[0.9444] font-normal whitespace-nowrap text-[rgba(0,27,20,0.7)] italic lg:text-[2rem] lg:leading-[1.5]">
            {data.prizesEyebrow}
          </p>
        )}
        {/* mobile: Figma's title box is 68px for two 35.4px lines */}
        <h2 style={at(0.12, 1)} className="tc-rv-rise mb-0 h-[8.5rem] text-center font-geist text-[4.25rem] leading-[1.04] font-medium tracking-[-0.1913rem] whitespace-pre text-[#001b14] lg:h-auto lg:text-[6rem] lg:leading-[1.15] lg:tracking-[-0.3rem]">
          <span className="relative font-blacker leading-[0] font-normal text-[#228e71] italic lg:tracking-[-0.18rem] lg:text-[#003828]">
            <SelectionHighlight
              {...TITLE_SELECTION}
              fill="rgba(34,142,113,0.12)"
              mobileFill="rgba(34,142,113,0.1)"
              handle="#228e71"
              hatch="/trading-competition/highlight-hatch-bonus.png"
              delay={0.85}
            />
            <span className="relative font-blacker">{data.prizesTitleHighlight}</span>
          </span>
          {/* mobile: own line; desktop: Figma's two serif spaces + one sans space */}
          <br className="lg:hidden" />
          <span className="hidden font-blacker leading-[0] font-normal italic lg:inline">{"  "}</span>
          <span className="hidden lg:inline">{" "}</span>
          {data.prizesTitleAfter}
        </h2>
      </div>

      {/* ── Desktop: the ring pinned mid-screen, cards arriving one band per scroll step ── */}
      <div
        ref={trackRef}
        className="relative hidden w-[150rem] lg:block"
        style={{ height: reduced ? "95.75rem" : `calc(95.75rem + ${n * STEP_VH}vh)` }}>
        <div ref={stageRef} className="sticky h-[95.75rem] w-[150rem]" style={{ top: "max(7.375rem, calc(50vh - 47.875rem))" }}>
          <div className="absolute top-[15.375rem] left-[42.5rem] h-[65rem] w-[65rem]">
            <PrizeRing fractions={fractions} filled={desktopFilled} active={desktopActive} className="h-full w-full" />
            <RingCenter total={desktopTotal} label={data.prizesPoolLabel} />
          </div>
          {bands.map((band, i) => {
            const [left, top] = DESKTOP_SPOTS[i] ?? [0, 0];
            const shown = i < revealed;
            const fromLeft = left < 50;
            return (
              <div
                key={band._key ?? i}
                className="absolute transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.22,0.61,0.36,1)] motion-reduce:transition-none"
                style={{
                  left: `${left}rem`,
                  top: `${top}rem`,
                  opacity: shown ? 1 : 0,
                  transform: shown ? "none" : `translate(${fromLeft ? -3 : 3}rem, 2rem)`,
                }}>
                <PrizeCard band={band} index={i} variant="desktop" {...cardProps} />
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Mobile: the ring sticks under the nav; the cards stack under it ── */}
      <div className="relative w-full lg:hidden">
        {/* the negative margin keeps Figma's resting spot; the padding is air under the nav while pinned */}
        <div className="sticky top-[7.625rem] z-10 -mt-[1.5rem] flex justify-center bg-white pt-[1.5rem] pb-[1.5rem]">
          <div className="relative h-[42.5rem] w-[42.5rem]">
            <PrizeRing fractions={fractions} filled={mobileFilled} active={mobileActive} className="h-full w-full" />
            <RingCenter total={mobileTotal} label={data.prizesPoolLabel} mobile />
          </div>
        </div>
        {/* bottom room so the last card can pin before the whole stack leaves; with reduced motion it is a plain list */}
        <div className="flex w-full flex-col gap-[1.5rem] pt-[2.5rem] pb-[20rem]">
          {bands.map((band, i) => (
            <div
              key={band._key ?? i}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className={cn(!reduced && "sticky")}
              style={reduced ? undefined : { top: `${STACK_TOP_REM}rem`, zIndex: i + 1 }}>
              <div
                ref={(el) => {
                  cardInnerRefs.current[i] = el;
                }}
                className="origin-top will-change-transform">
                <PrizeCard band={band} index={i} variant="mobile" {...cardProps} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.prizesNote && (
        <p className="mb-0 w-full text-center font-geist text-[1.875rem] leading-[normal] font-normal text-[rgba(0,27,20,0.66)] lg:w-[73.75rem] lg:text-[2.25rem]">
          <Bold text={data.prizesNote} />
        </p>
      )}
    </section>
  );
}
